import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Avatar } from "@/components/Avatar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { IdentificationBadge, EnvelopeSimple, CurrencyDollar, ShieldCheck, ArrowLeft, Camera, Trash, DownloadSimple, FileText, Receipt } from "@phosphor-icons/react";

function Row({ icon, label, value, testid }) {
  return (
    <div className="flex items-center justify-between py-4 border-b border-border last:border-0">
      <div className="flex items-center gap-3 label-caps">{icon} {label}</div>
      <div className="font-mono text-sm font-semibold" data-testid={testid}>{value}</div>
    </div>
  );
}

export default function Profile() {
  const { user, setUser } = useAuth();
  const [me, setMe] = useState(user);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const refreshMe = useCallback(async () => {
    const r = await api.get("/auth/me");
    setMe(r.data);
    setUser(r.data);
  }, [setUser]);

  useEffect(() => { refreshMe().catch(() => {}); }, [refreshMe]);

  const p = me || user;
  const isEmployee = p?.role === "employee";

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Please choose a JPEG, PNG or WEBP image");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshMe();
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const removePhoto = async () => {
    setBusy(true);
    try {
      await api.delete("/profile/photo");
      await refreshMe();
      toast.success("Profile photo removed");
    } catch {
      toast.error("Could not remove photo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout title="My Profile">
      <div className="max-w-3xl">
        <Link to={isEmployee ? "/" : "/admin"} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6" data-testid="profile-back-link">
          <ArrowLeft size={16} /> Back to dashboard
        </Link>

        <div className="flex items-center gap-5 mb-8">
          <div className="relative">
            <Avatar userId={p?.id} hasPhoto={p?.has_photo} updatedAt={p?.avatar_updated} name={p?.name} size={80} testid="profile-avatar" />
            <button onClick={onPick} disabled={busy} className="absolute -bottom-2 -right-2 w-8 h-8 bg-primary text-primary-foreground flex items-center justify-center border border-background disabled:opacity-50" title="Change photo" data-testid="upload-photo-button">
              <Camera size={16} weight="bold" />
            </button>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} data-testid="photo-file-input" />
          </div>
          <div>
            <h1 className="font-display font-bold text-3xl tracking-tighter" data-testid="profile-name">{p?.name}</h1>
            <div className="label-caps mt-1">{isEmployee ? "Contractor" : p?.role}</div>
            {p?.has_photo && (
              <button onClick={removePhoto} disabled={busy} className="mt-2 inline-flex items-center gap-1 text-xs text-destructive hover:underline disabled:opacity-50" data-testid="remove-photo-button">
                <Trash size={12} /> Remove photo
              </button>
            )}
          </div>
        </div>

        <Tabs defaultValue="details">
          {isEmployee && (
            <TabsList className="rounded-none bg-muted/60 p-1">
              <TabsTrigger value="details" className="rounded-none" data-testid="profile-tab-details">Details</TabsTrigger>
              <TabsTrigger value="tax" className="rounded-none" data-testid="profile-tab-tax">
                Tax (W-9){!p?.w9_completed && <span className="ml-2 w-2 h-2 rounded-full bg-destructive inline-block" data-testid="w9-incomplete-dot" />}
              </TabsTrigger>
              <TabsTrigger value="documents" className="rounded-none" data-testid="profile-tab-documents">Documents</TabsTrigger>
            </TabsList>
          )}

          <TabsContent value="details" className="mt-6">
            <Card className="rounded-none border-border p-6" data-testid="profile-card">
              {isEmployee && (
                <Row icon={<IdentificationBadge size={14} />} label="Title" value={p?.title || "—"} testid="profile-title" />
              )}
              <Row icon={<EnvelopeSimple size={14} />} label="Email" value={p?.email} testid="profile-email" />
              {isEmployee && (
                <Row icon={<CurrencyDollar size={14} />} label="Hourly Rate" value={`$${(p?.hourly_rate ?? 0).toFixed(2)}/hr`} testid="profile-rate" />
              )}
            </Card>
            <div className="mt-6 flex items-center gap-3">
              <Link to="/change-password">
                <Button variant="outline" className="rounded-none" data-testid="profile-change-password">
                  <ShieldCheck size={16} className="mr-2" /> Change password
                </Button>
              </Link>
              {isEmployee && (
                <p className="text-sm text-muted-foreground">Your pay rate and title are managed by your administrator.</p>
              )}
            </div>
          </TabsContent>

          {isEmployee && (
            <TabsContent value="tax" className="mt-6">
              <W9Panel onSaved={refreshMe} />
            </TabsContent>
          )}

          {isEmployee && (
            <TabsContent value="documents" className="mt-6">
              <DocumentsPanel />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Layout>
  );
}

function DocumentsPanel() {
  const [freq, setFreq] = useState("weekly");
  const [periods, setPeriods] = useState([]);
  const year = new Date().getFullYear();

  useEffect(() => {
    api.get(`/payroll/periods?frequency=${freq}&year=${year}`).then((r) => setPeriods(r.data.periods)).catch(() => {});
  }, [freq, year]);

  const download = async (url, filename, label) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const u = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = u; a.download = filename; a.click();
      URL.revokeObjectURL(u);
      toast.success(`${label} downloaded`);
    } catch {
      toast.error("Could not download");
    }
  };

  return (
    <div className="space-y-6" data-testid="documents-panel">
      <Card className="rounded-none border-border p-6">
        <div className="flex items-center gap-2 label-caps mb-4"><FileText size={14} /> Tax Forms</div>
        <div className="flex items-center justify-between">
          <div className="font-mono text-sm">Form 1099-NEC · {year}</div>
          <Doc1099Button year={year} />
        </div>
      </Card>

      <Card className="rounded-none border-border p-0">
        <div className="p-6 border-b border-border flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2 label-caps"><Receipt size={14} /> Pay Stubs</div>
          <div className="flex items-center border border-border" data-testid="doc-freq-toggle">
            {["weekly", "biweekly"].map((f) => (
              <button key={f} onClick={() => setFreq(f)} className={`px-4 py-2 text-xs uppercase tracking-[0.15em] font-semibold transition-colors ${freq === f ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`doc-freq-${f}`}>{f}</button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-mono">
            <thead>
              <tr className="label-caps text-left border-b border-border bg-muted/40">
                <th className="px-6 py-3 font-semibold">Pay Period</th>
                <th className="px-6 py-3 font-semibold text-right">Hours</th>
                <th className="px-6 py-3 font-semibold text-right">Gross Pay</th>
                <th className="px-6 py-3 font-semibold text-right">Stub</th>
              </tr>
            </thead>
            <tbody>
              {periods.length === 0 && <tr><td colSpan={4} className="px-6 py-10 text-center text-muted-foreground">No pay periods yet.</td></tr>}
              {periods.map((pr, i) => (
                <tr key={i} className={i % 2 ? "bg-muted/20" : ""} data-testid={`doc-stub-row-${i}`}>
                  <td className="px-6 py-3">{pr.start} → {pr.end}</td>
                  <td className="px-6 py-3 text-right">{pr.total_hours.toFixed(2)}</td>
                  <td className="px-6 py-3 text-right font-bold">${pr.total_pay.toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                  <td className="px-6 py-3">
                    <div className="flex justify-end">
                      <Button variant="outline" size="sm" className="rounded-none" onClick={() => download(`/paystub/pdf?start=${pr.start}&end=${pr.end}`, `paystub-${pr.start}.pdf`, "Pay stub")} data-testid={`doc-download-stub-${i}`}>
                        <DownloadSimple size={14} className="mr-1.5" /> Pay Stub
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

const ENTITY_OPTIONS = [
  ["individual", "Individual"],
  ["sole_prop", "Sole proprietor"],
  ["single_llc", "Single-member LLC"],
  ["llc", "LLC (C / S / Partnership)"],
  ["c_corp", "C Corporation"],
  ["s_corp", "S Corporation"],
  ["partnership", "Partnership"],
  ["trust", "Trust / estate"],
  ["other", "Other"],
];

function W9Panel({ onSaved }) {
  const empty = { legal_name: "", tin: "", entity_type: "individual", address_line1: "", address_line2: "", city: "", state: "", zip: "" };
  const [f, setF] = useState(empty);
  const [completed, setCompleted] = useState(false);
  const [masked, setMasked] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    api.get("/profile/w9").then((r) => {
      if (r.data.w9_completed) {
        setCompleted(true);
        setMasked(r.data.tin_masked);
        setF({ ...empty, ...r.data, tin: "" });
      } else {
        setEditing(true);
      }
    }).catch(() => setEditing(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/profile/w9", f);
      setCompleted(true);
      setMasked(data.tin_masked);
      setF((prev) => ({ ...prev, state: prev.state.trim().toUpperCase(), tin: "" }));
      setEditing(false);
      toast.success("W-9 information saved");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save W-9");
    } finally {
      setBusy(false);
    }
  };

  const entityLabel = ENTITY_OPTIONS.find(([v]) => v === f.entity_type)?.[1] || f.entity_type;

  if (completed && !editing) {
    return (
      <Card className="rounded-none border-border p-6" data-testid="w9-summary">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 label-caps"><FileText size={14} /> W-9 on file</div>
          <Button variant="outline" size="sm" className="rounded-none" onClick={() => { setEditing(true); setF({ ...f, tin: "" }); }} data-testid="w9-edit-button">Edit</Button>
        </div>
        <Row icon={<IdentificationBadge size={14} />} label="Legal name" value={f.legal_name} testid="w9-legal-name" />
        <Row icon={<FileText size={14} />} label="Entity type" value={entityLabel} testid="w9-entity" />
        <Row icon={<ShieldCheck size={14} />} label="TIN" value={masked || "—"} testid="w9-tin" />
        <Row icon={<EnvelopeSimple size={14} />} label="Address" value={`${f.address_line1}${f.address_line2 ? ", " + f.address_line2 : ""}, ${f.city}, ${f.state} ${f.zip}`} testid="w9-address" />
      </Card>
    );
  }

  return (
    <Card className="rounded-none border-border p-6" data-testid="w9-form">
      <div className="flex items-center gap-2 label-caps mb-2"><FileText size={14} /> Substitute W-9</div>
      <p className="text-sm text-muted-foreground mb-6">Required for your 1099-NEC. Your TIN is encrypted and only the last 4 digits are ever displayed.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Label className="label-caps">Legal name (as on tax return)</Label>
          <Input className="rounded-none mt-2" value={f.legal_name} onChange={upd("legal_name")} data-testid="w9-input-legal-name" />
        </div>
        <div>
          <Label className="label-caps">Federal tax classification</Label>
          <select className="mt-2 w-full h-10 border border-input bg-background px-3 text-sm rounded-none" value={f.entity_type} onChange={upd("entity_type")} data-testid="w9-input-entity">
            {ENTITY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <Label className="label-caps">TIN (SSN or EIN)</Label>
          <Input className="rounded-none mt-2 font-mono" placeholder="XXX-XX-XXXX" value={f.tin} onChange={upd("tin")} data-testid="w9-input-tin" />
        </div>
        <div className="sm:col-span-2">
          <Label className="label-caps">Address line 1</Label>
          <Input className="rounded-none mt-2" value={f.address_line1} onChange={upd("address_line1")} data-testid="w9-input-address1" />
        </div>
        <div className="sm:col-span-2">
          <Label className="label-caps">Address line 2 (optional)</Label>
          <Input className="rounded-none mt-2" value={f.address_line2} onChange={upd("address_line2")} data-testid="w9-input-address2" />
        </div>
        <div>
          <Label className="label-caps">City</Label>
          <Input className="rounded-none mt-2" value={f.city} onChange={upd("city")} data-testid="w9-input-city" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="label-caps">State</Label>
            <Input className="rounded-none mt-2 uppercase" maxLength={2} value={f.state} onChange={upd("state")} data-testid="w9-input-state" />
          </div>
          <div>
            <Label className="label-caps">ZIP</Label>
            <Input className="rounded-none mt-2 font-mono" value={f.zip} onChange={upd("zip")} data-testid="w9-input-zip" />
          </div>
        </div>
      </div>
      <div className="mt-6 flex items-center gap-3">
        <Button className="rounded-none" disabled={busy} onClick={save} data-testid="w9-save-button">{busy ? "Saving…" : "Save W-9"}</Button>
        {completed && <Button variant="ghost" className="rounded-none" onClick={() => setEditing(false)} data-testid="w9-cancel-button">Cancel</Button>}
      </div>
    </Card>
  );
}

function Doc1099Button({ year }) {
  const { user } = useAuth();
  const download = async () => {
    try {
      const res = await api.get(`/tax/1099/pdf?employee_id=${user.id}&year=${year}`, { responseType: "blob" });
      const u = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = u; a.download = `1099-NEC-${year}.pdf`; a.click();
      URL.revokeObjectURL(u);
      toast.success("1099-NEC downloaded");
    } catch {
      toast.error("Could not download");
    }
  };
  return (
    <Button variant="outline" size="sm" className="rounded-none" onClick={download} data-testid="doc-download-1099">
      <DownloadSimple size={14} className="mr-1.5" /> Download
    </Button>
  );
}
